/**
 * @module client
 *
 * @todo Move `MessageTransport` into shared space.
 */
import {findMatchInEnum, forEachInEnum} from '../actions/helper';
import {validatedAction} from '../actions/validator';
import {throwError, ActionType as ErrorTypes} from '../actions/Error';
import {ActionType as PrintActionType} from '../actions/Print';
import {fromWindow} from '../host/MessageTransport';
import {addAndRemoveFromCollection} from '../util/collection';
import {isDevelopmentClient} from '../util/env';
import {handleAppPrint} from './print';
import {getLocation, redirect, shouldRedirect} from './redirect';
import {
  ActionListener,
  ActionListenersMap,
  AppConfig,
  ClientApplication,
  ClientApplicationCreator,
  ClientApplicationTransportInjector,
  Handler,
  Unsubscribe,
} from './types';

export interface Params {
  [key: string]: string;
}

function redirectHandler(hostFrame: Window, config: AppConfig) {
  const {apiKey, shopOrigin, forceRedirect = !isDevelopmentClient} = config;
  const location = getLocation();
  if (forceRedirect && shouldRedirect(hostFrame) && apiKey && shopOrigin) {
    const url = `https://${shopOrigin}/admin/apps/${apiKey}${location.pathname}${location.search ||
      ''}`;
    redirect(url);
  }
}

function appSetUp(app: ClientApplication<any>) {
  app.subscribe(PrintActionType.APP, handleAppPrint);
}

export function getUrlParams() {
  const location = getLocation();
  const hashes = location.search.slice(location.search.indexOf('?') + 1).split('&');
  const params: Params = {};

  return hashes.reduce((acc, hash) => {
    const [key, val] = hash.split('=');

    return {
      ...acc,
      [decodeURIComponent(key)]: decodeURIComponent(val),
    };
  }, params);
}

export function getShopOrigin() {
  const params = getUrlParams();

  return params.shop;
}

export const createClientApp: ClientApplicationTransportInjector = transport => {
  const getStateListeners: Function[] = [];
  const listeners: ActionListener[] = [];
  const actionListeners: ActionListenersMap = {};

  const invokeCallbacks = (type: string, payload?: any) => {
    let hasCallback = false;
    if (actionListeners.hasOwnProperty(type)) {
      for (const listener of actionListeners[type]) {
        const {id, callback} = listener;
        const matchId = payload && payload.id === id;
        if (matchId || !id) {
          callback(payload);
          hasCallback = true;
        }
      }
    }
    if (hasCallback) {
      return;
    }
    // Throw an error if there are no subscriptions to this error
    const errorType = findMatchInEnum(ErrorTypes, type);
    if (errorType) {
      throwError(errorType, payload);
    }
  };

  const handler: Handler = event => {
    const action = event.data;

    switch (action.type) {
      case 'getState':
        const resolvers = getStateListeners.splice(0);
        resolvers.forEach(resolver => resolver(action.payload));
        break;

      case 'dispatch':
        const {payload} = action;
        invokeCallbacks(payload.type, payload.payload);
        listeners.forEach(listener => listener.callback(payload));
        break;

      default:
      // Silently swallow unknown actions
    }
  };

  transport.subscribe(handler);

  return (config: AppConfig /*initialState*/) => {
    redirectHandler(transport.hostFrame, config);

    const app: ClientApplication<any> = {
      localOrigin: transport.localOrigin,

      dispatch<A>(action: A) {
        const validAction = validatedAction(action, transport.localOrigin);
        const augmentedAction = {
          payload: validAction,
          source: config,
          type: 'dispatch',
        };

        handler({data: augmentedAction});
        transport.dispatch(augmentedAction);

        return validAction as A;
      },

      getState(query?: string) {
        return new Promise<any>(resolve => {
          getStateListeners.push(resolve);
          transport.dispatch({
            payload: undefined,
            source: config,
            type: 'getState',
          });
        }).then((state: any) => {
          if (query) {
            return query.split('.').reduce((value, key) => {
              if (typeof state !== 'object' || Array.isArray(state)) {
                return undefined;
              }
              value = state[key];
              state = value;

              return value;
            }, undefined);
          }

          return state;
        });
      },

      subscribe() {
        if (arguments.length < 2) {
          return addAndRemoveFromCollection(listeners, {callback: arguments[0]});
        }

        const eventNameSpace = arguments[0];
        const callback = arguments[1];
        const id = arguments[2];
        const actionCallback: ActionListener = {callback, id};
        if (!actionListeners.hasOwnProperty(eventNameSpace)) {
          actionListeners[eventNameSpace] = [];
        }

        return addAndRemoveFromCollection(actionListeners[eventNameSpace], actionCallback);
      },

      error(listener, id?) {
        const unsubscribeCb: Unsubscribe[] = [];
        forEachInEnum(ErrorTypes, eventNameSpace => {
          // tslint:disable-next-line:no-invalid-this
          unsubscribeCb.push(this.subscribe(eventNameSpace, listener, id));
        });

        return () => {
          unsubscribeCb.forEach(unsubscribe => unsubscribe());
        };
      },
    };

    appSetUp(app);

    return app;
  };
};

export function createAppWrapper(frame: Window, localOrigin?: string): ClientApplicationCreator {
  const transport = fromWindow(frame, localOrigin || window.location.origin);

  return createClientApp(transport);
}

export default function(config: AppConfig) {
  return createAppWrapper(window.top)(config);
}



// WEBPACK FOOTER //
// ./src/client/Client.ts